// Wave 7 — client-side SMPL fit from MediaPipe Pose world landmarks.
//
// Replaces the server-side 4D-Humans backend. The avatar components
// (AvatarPanel, AvatarOverlay) consume a SMPLFitResult shape; this module
// produces one from each frame's 33 MediaPipe worldLandmarks (3D in metres,
// hip-centered origin).
//
// Math: "twist-free per-bone, parent-first" — for each SMPL joint J we
// determine the bone's observed world direction from a pair of (parent,
// child) MediaPipe landmarks, transform it into J's parent's local frame,
// then compute the minimum rotation from the rest-pose bone direction to
// that observed direction via Quaternion.setFromUnitVectors. World
// rotations accumulate down the hierarchy so each bone's parent frame is
// correct. The pelvis (joint 0) gets its rotation from a body coordinate
// frame built from hip-to-hip and hip-to-shoulder axes.
//
// Smoothing: One-Euro per landmark per axis (33 × 3 = 99 filters), input
// side only. Output quaternions stay raw — slerp-near-singularity is
// noisier than landmark filtering. Cutoffs tuned per-group so cycling
// cadence (~1.5 Hz at 90 rpm) passes through knees + ankles + wrists +
// elbows but the torso anchors stay glassy-smooth.
//
// Visibility dropout: when either anchor landmark drops below 0.5
// visibility, we hold the previous frame's local rotation and decay it
// slowly toward identity. An occluded arm relaxes naturally instead of
// snapping to T-pose.

import * as THREE from 'three';
import { WorldLandmark } from './poseDetector';
import { SMPLFitResult } from '../services/visionAdvanced';
import { OneEuroFilter } from './oneEuro';
import { isCalibrationLocked } from './calibration';

const SMPL_PARENTS = [
  -1, 0, 0, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 9, 9, 12, 13, 14, 16, 17, 18, 19, 20, 21,
];

// Bone offsets (metres) from each joint's parent. Mirrored from
// AvatarPanel.tsx; the avatar uses the same constants for rendering.
const SMPL_BONE_OFFSETS: Array<[number, number, number]> = [
  [0, 0, 0],          [0.07, -0.07, 0],   [-0.07, -0.07, 0],  [0, 0.10, 0],
  [0.05, -0.40, 0],   [-0.05, -0.40, 0],  [0, 0.13, 0],       [0.02, -0.43, 0],
  [-0.02, -0.43, 0],  [0, 0.10, 0],       [0, -0.05, 0.12],   [0, -0.05, 0.12],
  [0, 0.15, 0],       [0.07, 0.07, 0],    [-0.07, 0.07, 0],   [0, 0.10, 0],
  [0.15, 0, 0],       [-0.15, 0, 0],      [0.30, 0, 0],       [-0.30, 0, 0],
  [0.27, 0, 0],       [-0.27, 0, 0],      [0.08, 0, 0],       [-0.08, 0, 0],
];

// Virtual landmarks computed from MediaPipe primitives. The string keys
// are how MP_BONE_TARGETS references them — keeps the mapping table tidy.
type MpAnchor =
  | number // direct MediaPipe landmark index (0..32)
  | 'midhip'
  | 'midshoulder'
  | 'midspine_lo'
  | 'midspine_hi'
  | 'midneck';

interface MpBoneTarget {
  kind: 'root' | 'identity' | 'direct';
  parent?: MpAnchor;
  child?: MpAnchor;
}

// One entry per SMPL joint (index = SMPL J). The bone "at" joint J runs
// from J's SMPL parent (rest offset = SMPL_BONE_OFFSETS[J]) to J itself.
// We observe that bone's world direction as MP[child] - MP[parent].
const MP_BONE_TARGETS: MpBoneTarget[] = [
  { kind: 'root' },                                                    // 0  pelvis
  { kind: 'direct', parent: 'midhip',       child: 23 },               // 1  L hip
  { kind: 'direct', parent: 'midhip',       child: 24 },               // 2  R hip
  { kind: 'direct', parent: 'midhip',       child: 'midspine_lo' },    // 3  spine1
  { kind: 'direct', parent: 23,             child: 25 },               // 4  L upper leg
  { kind: 'direct', parent: 24,             child: 26 },               // 5  R upper leg
  { kind: 'direct', parent: 'midspine_lo',  child: 'midspine_hi' },    // 6  spine2
  { kind: 'direct', parent: 25,             child: 27 },               // 7  L lower leg
  { kind: 'direct', parent: 26,             child: 28 },               // 8  R lower leg
  { kind: 'direct', parent: 'midspine_hi',  child: 'midshoulder' },    // 9  spine3
  { kind: 'identity' },                                                // 10 L foot
  { kind: 'identity' },                                                // 11 R foot
  { kind: 'direct', parent: 'midshoulder',  child: 'midneck' },        // 12 neck
  { kind: 'identity' },                                                // 13 L collar
  { kind: 'identity' },                                                // 14 R collar
  { kind: 'direct', parent: 'midneck',      child: 0 },                // 15 head
  { kind: 'direct', parent: 'midshoulder',  child: 11 },               // 16 L shoulder
  { kind: 'direct', parent: 'midshoulder',  child: 12 },               // 17 R shoulder
  { kind: 'direct', parent: 11,             child: 13 },               // 18 L elbow
  { kind: 'direct', parent: 12,             child: 14 },               // 19 R elbow
  { kind: 'direct', parent: 13,             child: 15 },               // 20 L wrist
  { kind: 'direct', parent: 14,             child: 16 },               // 21 R wrist
  { kind: 'identity' },                                                // 22 L hand
  { kind: 'identity' },                                                // 23 R hand
];

// Per-landmark-group filter cutoffs. The default targets all unspecified
// indices.
interface FilterSpec { minCutoff: number; beta: number; }
const FILTER_DEFAULT: FilterSpec = { minCutoff: 1.0, beta: 0.05 };
const FILTER_TORSO:   FilterSpec = { minCutoff: 0.8, beta: 0.04 };
const FILTER_CYCLE:   FilterSpec = { minCutoff: 1.5, beta: 0.07 };
function specForLandmark(idx: number): FilterSpec {
  if (idx === 11 || idx === 12 || idx === 23 || idx === 24) return FILTER_TORSO;
  // Knees, elbows, wrists, ankles, hips' descendants — anything that moves
  // with cycling cadence.
  if ((idx >= 13 && idx <= 16) || (idx >= 25 && idx <= 28)) return FILTER_CYCLE;
  return FILTER_DEFAULT;
}

const MIN_VIS = 0.5;
// When an anchor drops below MIN_VIS we keep the previous local quaternion
// but pull it toward identity by this fraction per frame. 0.0 = never
// relax (stays frozen), 1.0 = snap to identity. 0.05 gives ~1s decay.
const HOLD_RELAX_PER_FRAME = 0.05;

// Reference dimensions baked into SMPL_BONE_OFFSETS. We measure the
// rider's actual dimensions from MediaPipe and scale offsets by
// (measured / reference). Numbers are derived from the constants above:
// shoulder_width = 0.07 * 2 = 0.14, hip_width = 0.07 * 2 = 0.14, etc.
const REF_DIMS = {
  shoulder_width: 0.14,
  hip_width: 0.14,
  spine_length: 0.10 + 0.13 + 0.10 + 0.15, // pelvis→spine1→spine2→spine3→neck
  upper_arm: 0.30,
  forearm: 0.27,
  thigh: 0.40,
  shin: 0.43,
  head_radius: 0.10,
};

// EMA alpha for body-dim convergence. 0.05 → 95% converged in ~60 frames
// (~2s at 30 Hz), then stable.
const DIMS_EMA = 0.05;

export class PoseToSmplFitter {
  // Per-landmark, per-axis filters. lmFilters[33][3].
  private readonly lmFilters: OneEuroFilter[][];
  // Last good local quaternion per joint — used when visibility drops.
  private readonly lastLocal: THREE.Quaternion[];
  // Per-rider EMA'd body dimensions in metres. null until first frame.
  private dims: {
    shoulder_width: number;
    hip_width: number;
    spine_length: number;
    upper_arm: number;
    forearm: number;
    thigh: number;
    shin: number;
    height: number;
  } | null = null;

  constructor() {
    this.lmFilters = [];
    for (let i = 0; i < 33; i++) {
      const { minCutoff, beta } = specForLandmark(i);
      this.lmFilters.push([
        new OneEuroFilter({ minCutoff, beta, acceptNegatives: true }),
        new OneEuroFilter({ minCutoff, beta, acceptNegatives: true }),
        new OneEuroFilter({ minCutoff, beta, acceptNegatives: true }),
      ]);
    }
    this.lastLocal = Array.from({ length: 24 }, () => new THREE.Quaternion());
  }

  reset(): void {
    for (const fs of this.lmFilters) fs.forEach((f) => f.reset());
    for (const q of this.lastLocal) q.identity();
    this.dims = null;
  }

  /** Build a per-rider scaled bone-offsets array from the EMA'd
   *  dimensions. Each component of SMPL_BONE_OFFSETS gets multiplied by
   *  the appropriate ratio (measured/reference). Returns an array shaped
   *  exactly like SMPL_BONE_OFFSETS so AvatarPanel/AvatarOverlay can drop
   *  it in as-is. */
  private scaledOffsets(): Array<[number, number, number]> {
    if (!this.dims) return SMPL_BONE_OFFSETS;
    const d = this.dims;
    const swR = d.shoulder_width / REF_DIMS.shoulder_width;
    const hwR = d.hip_width      / REF_DIMS.hip_width;
    const spR = d.spine_length   / REF_DIMS.spine_length;
    const uaR = d.upper_arm      / REF_DIMS.upper_arm;
    const faR = d.forearm        / REF_DIMS.forearm;
    const thR = d.thigh          / REF_DIMS.thigh;
    const shR = d.shin           / REF_DIMS.shin;
    // Wrap helpers — each return a [x, y, z] triple scaled per axis.
    const scaleX = (i: number, r: number): [number, number, number] => {
      const o = SMPL_BONE_OFFSETS[i];
      return [o[0] * r, o[1], o[2]];
    };
    const scaleY = (i: number, r: number): [number, number, number] => {
      const o = SMPL_BONE_OFFSETS[i];
      return [o[0], o[1] * r, o[2]];
    };
    const scaleAll = (i: number, r: number): [number, number, number] => {
      const o = SMPL_BONE_OFFSETS[i];
      return [o[0] * r, o[1] * r, o[2] * r];
    };
    const out: Array<[number, number, number]> = SMPL_BONE_OFFSETS.map((o) =>
      [o[0], o[1], o[2]] as [number, number, number],
    );
    // Hip-width bones (joints 1, 2 = L/R hip — X offset from pelvis).
    out[1] = scaleX(1, hwR);
    out[2] = scaleX(2, hwR);
    // Shoulder-width bones (joints 13, 14 = L/R collar — X offset from spine3).
    out[13] = scaleX(13, swR);
    out[14] = scaleX(14, swR);
    // Spine chain (joints 3, 6, 9, 12 = spine1, spine2, spine3, neck —
    // Y offset upward). Scale each segment by overall spine ratio.
    out[3]  = scaleY(3,  spR);
    out[6]  = scaleY(6,  spR);
    out[9]  = scaleY(9,  spR);
    out[12] = scaleY(12, spR);
    // Upper-arm bones (joints 16, 17 — X offset from collar).
    out[16] = scaleX(16, uaR);
    out[17] = scaleX(17, uaR);
    // Forearm bones (joints 18, 19 — X offset from upper arm).
    out[18] = scaleX(18, faR);
    out[19] = scaleX(19, faR);
    // Hand offsets (joints 20, 21 — small, scale with forearm).
    out[20] = scaleX(20, faR);
    out[21] = scaleX(21, faR);
    // Thigh bones (joints 4, 5 — Y offset downward from hip).
    out[4] = scaleY(4, thR);
    out[5] = scaleY(5, thR);
    // Shin bones (joints 7, 8 — Y offset downward from knee).
    out[7] = scaleY(7, shR);
    out[8] = scaleY(8, shR);
    // Foot offsets (joints 10, 11 — small, scale with shin).
    out[10] = scaleAll(10, shR);
    out[11] = scaleAll(11, shR);
    return out;
  }

  /** Update EMA'd body dimensions from the current frame's landmarks.
   *  We only update measurements when both anchor landmarks are visible;
   *  partial updates would skew the EMA. */
  private updateDims(smoothed: THREE.Vector3[], world: WorldLandmark[]): void {
    const vis = (i: number) => world[i].visibility;
    const dist = (a: THREE.Vector3, b: THREE.Vector3) => a.distanceTo(b);
    const sample: Partial<NonNullable<PoseToSmplFitter['dims']>> = {};
    if (vis(11) >= MIN_VIS && vis(12) >= MIN_VIS) {
      sample.shoulder_width = dist(smoothed[11], smoothed[12]);
    }
    if (vis(23) >= MIN_VIS && vis(24) >= MIN_VIS) {
      sample.hip_width = dist(smoothed[23], smoothed[24]);
    }
    if (vis(11) >= MIN_VIS && vis(12) >= MIN_VIS && vis(23) >= MIN_VIS && vis(24) >= MIN_VIS) {
      const midShoulder = smoothed[11].clone().add(smoothed[12]).multiplyScalar(0.5);
      const midHip      = smoothed[23].clone().add(smoothed[24]).multiplyScalar(0.5);
      sample.spine_length = dist(midShoulder, midHip);
    }
    // Average left/right arm; bilateral symmetry gives us a better estimate
    // than either side alone.
    const uaSamples: number[] = [];
    if (vis(11) >= MIN_VIS && vis(13) >= MIN_VIS) uaSamples.push(dist(smoothed[11], smoothed[13]));
    if (vis(12) >= MIN_VIS && vis(14) >= MIN_VIS) uaSamples.push(dist(smoothed[12], smoothed[14]));
    if (uaSamples.length) sample.upper_arm = uaSamples.reduce((a, b) => a + b, 0) / uaSamples.length;
    const faSamples: number[] = [];
    if (vis(13) >= MIN_VIS && vis(15) >= MIN_VIS) faSamples.push(dist(smoothed[13], smoothed[15]));
    if (vis(14) >= MIN_VIS && vis(16) >= MIN_VIS) faSamples.push(dist(smoothed[14], smoothed[16]));
    if (faSamples.length) sample.forearm = faSamples.reduce((a, b) => a + b, 0) / faSamples.length;
    const thSamples: number[] = [];
    if (vis(23) >= MIN_VIS && vis(25) >= MIN_VIS) thSamples.push(dist(smoothed[23], smoothed[25]));
    if (vis(24) >= MIN_VIS && vis(26) >= MIN_VIS) thSamples.push(dist(smoothed[24], smoothed[26]));
    if (thSamples.length) sample.thigh = thSamples.reduce((a, b) => a + b, 0) / thSamples.length;
    const shSamples: number[] = [];
    if (vis(25) >= MIN_VIS && vis(27) >= MIN_VIS) shSamples.push(dist(smoothed[25], smoothed[27]));
    if (vis(26) >= MIN_VIS && vis(28) >= MIN_VIS) shSamples.push(dist(smoothed[26], smoothed[28]));
    if (shSamples.length) sample.shin = shSamples.reduce((a, b) => a + b, 0) / shSamples.length;

    // Rider height = nose to ankle midpoint, with a head-radius adjustment
    // since nose is partway down the head. Approximate but stable.
    if (vis(0) >= MIN_VIS && vis(27) >= MIN_VIS && vis(28) >= MIN_VIS) {
      const midAnkle = smoothed[27].clone().add(smoothed[28]).multiplyScalar(0.5);
      sample.height = dist(smoothed[0], midAnkle) + REF_DIMS.head_radius;
    }

    // Wave 13 / Track E — once calibration is locked, freeze the dim EMA.
    // bone_offsets are derived from this.dims, so freezing here also freezes
    // bone_offsets without touching the offset-derivation code path.
    if (isCalibrationLocked() && this.dims) {
      return;
    }

    // EMA into persistent dims. First sample seeds the value.
    if (!this.dims) {
      // Need at least the core dims to seed. Use ref values for anything
      // missing on the first frame so we don't seed with zeros.
      this.dims = {
        shoulder_width: sample.shoulder_width ?? REF_DIMS.shoulder_width,
        hip_width:      sample.hip_width      ?? REF_DIMS.hip_width,
        spine_length:   sample.spine_length   ?? REF_DIMS.spine_length,
        upper_arm:      sample.upper_arm      ?? REF_DIMS.upper_arm,
        forearm:        sample.forearm        ?? REF_DIMS.forearm,
        thigh:          sample.thigh          ?? REF_DIMS.thigh,
        shin:           sample.shin           ?? REF_DIMS.shin,
        height:         sample.height         ?? 1.75,
      };
      return;
    }
    const ema = (cur: number, next: number | undefined) =>
      next == null ? cur : cur * (1 - DIMS_EMA) + next * DIMS_EMA;
    this.dims.shoulder_width = ema(this.dims.shoulder_width, sample.shoulder_width);
    this.dims.hip_width      = ema(this.dims.hip_width,      sample.hip_width);
    this.dims.spine_length   = ema(this.dims.spine_length,   sample.spine_length);
    this.dims.upper_arm      = ema(this.dims.upper_arm,      sample.upper_arm);
    this.dims.forearm        = ema(this.dims.forearm,        sample.forearm);
    this.dims.thigh          = ema(this.dims.thigh,          sample.thigh);
    this.dims.shin           = ema(this.dims.shin,           sample.shin);
    this.dims.height         = ema(this.dims.height,         sample.height);
  }

  /** Convert one frame's worldLandmarks into a SMPLFitResult. tMs is the
   *  monotonic timestamp from performance.now(). */
  fit(world: WorldLandmark[], tMs: number): SMPLFitResult {
    // 1. Smooth the landmarks. MediaPipe Pose world landmarks use the
    //    image convention: x right, y DOWN (positive Y toward the feet),
    //    z toward camera. SMPL/Three.js use y UP. We negate y at the read
    //    so all downstream math (root frame, bone directions, root
    //    translation) is in SMPL convention with no further flips.
    const smoothed: THREE.Vector3[] = new Array(33);
    for (let i = 0; i < 33; i++) {
      const lm = world[i];
      const fx = this.lmFilters[i];
      const sx = fx[0].update(lm.x, tMs) ?? lm.x;
      const sy = fx[1].update(lm.y, tMs) ?? lm.y;
      const sz = fx[2].update(lm.z, tMs) ?? lm.z;
      smoothed[i] = new THREE.Vector3(sx, -sy, sz);
    }

    // 2. Virtual landmarks. lerp factors chosen so the spine chain joints
    //    land roughly proportionally between hip and shoulder midpoints.
    const midhip = smoothed[23].clone().add(smoothed[24]).multiplyScalar(0.5);
    const midshoulder = smoothed[11].clone().add(smoothed[12]).multiplyScalar(0.5);
    const midspineLo = midhip.clone().lerp(midshoulder, 0.33);
    const midspineHi = midhip.clone().lerp(midshoulder, 0.66);
    const midneck = midshoulder.clone().lerp(smoothed[0], 0.6);

    // Wave 7.1 — update per-rider EMA'd body dimensions from this frame's
    // landmarks. Stabilizes quickly so the avatar matches the rider's
    // actual proportions.
    this.updateDims(smoothed, world);
    const offsets = this.scaledOffsets();

    const pointOf = (a: MpAnchor): THREE.Vector3 => {
      if (typeof a === 'number') return smoothed[a];
      if (a === 'midhip') return midhip;
      if (a === 'midshoulder') return midshoulder;
      if (a === 'midspine_lo') return midspineLo;
      if (a === 'midspine_hi') return midspineHi;
      return midneck; // 'midneck'
    };
    const visOf = (a: MpAnchor): number => {
      if (typeof a === 'number') return world[a].visibility;
      if (a === 'midhip') return Math.min(world[23].visibility, world[24].visibility);
      if (a === 'midshoulder') return Math.min(world[11].visibility, world[12].visibility);
      if (a === 'midspine_lo' || a === 'midspine_hi') {
        return Math.min(visOf('midhip'), visOf('midshoulder'));
      }
      return Math.min(visOf('midshoulder'), world[0].visibility); // midneck
    };

    // 3. Root orientation: build a body coordinate frame from hip + shoulder
    //    axes. X is across the body (right hip - left hip), Y up the spine
    //    (midhip - midshoulder, then flipped so up-is-up matches the frame
    //    convention).
    const xAxis = smoothed[24].clone().sub(smoothed[23]);
    if (xAxis.lengthSq() < 1e-6) xAxis.set(1, 0, 0);
    xAxis.normalize();
    const yAxis = midshoulder.clone().sub(midhip);
    if (yAxis.lengthSq() < 1e-6) yAxis.set(0, 1, 0);
    yAxis.normalize();
    // Orthogonalize: project Y component out of X to make them perpendicular.
    xAxis.addScaledVector(yAxis, -xAxis.dot(yAxis)).normalize();
    const zAxis = new THREE.Vector3().crossVectors(xAxis, yAxis).normalize();
    const rootBasis = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
    const rootWorld = new THREE.Quaternion().setFromRotationMatrix(rootBasis);

    // 4. Walk the SMPL hierarchy parent-first to compute local rotations.
    const worldQuats: THREE.Quaternion[] = Array.from({ length: 24 }, () => new THREE.Quaternion());
    const localQuats: THREE.Quaternion[] = Array.from({ length: 24 }, () => new THREE.Quaternion());
    worldQuats[0].copy(rootWorld);
    localQuats[0].copy(rootWorld);
    this.lastLocal[0].copy(rootWorld);

    const identity = new THREE.Quaternion();
    const restVec = new THREE.Vector3();
    const observed = new THREE.Vector3();
    const parentInv = new THREE.Quaternion();

    for (let j = 1; j < 24; j++) {
      const tgt = MP_BONE_TARGETS[j];
      const parent = SMPL_PARENTS[j];

      if (tgt.kind === 'identity') {
        localQuats[j].identity();
        worldQuats[j].copy(worldQuats[parent]);
        continue;
      }

      const offset = offsets[j];
      restVec.set(offset[0], offset[1], offset[2]);
      if (restVec.lengthSq() < 1e-8) {
        // Degenerate rest offset — no bone direction. Treat as identity.
        localQuats[j].identity();
        worldQuats[j].copy(worldQuats[parent]);
        continue;
      }
      restVec.normalize();

      const pa = tgt.parent!;
      const ch = tgt.child!;
      observed.copy(pointOf(ch)).sub(pointOf(pa));
      const observedLen = observed.length();

      const visible = visOf(pa) >= MIN_VIS && visOf(ch) >= MIN_VIS && observedLen > 1e-4;
      if (!visible) {
        // Hold the previous local rotation; relax slowly toward identity.
        localQuats[j].copy(this.lastLocal[j]).slerp(identity, HOLD_RELAX_PER_FRAME);
        worldQuats[j].multiplyQuaternions(worldQuats[parent], localQuats[j]);
        this.lastLocal[j].copy(localQuats[j]);
        continue;
      }
      observed.divideScalar(observedLen);

      // Bring observed direction into the parent joint's local frame.
      parentInv.copy(worldQuats[parent]).invert();
      observed.applyQuaternion(parentInv);

      // Minimum rotation from rest direction (in parent frame) to observed
      // direction (in parent frame). setFromUnitVectors handles the
      // antipodal edge case internally.
      localQuats[j].setFromUnitVectors(restVec, observed);
      worldQuats[j].multiplyQuaternions(worldQuats[parent], localQuats[j]);
      this.lastLocal[j].copy(localQuats[j]);
    }

    // 5. Flatten local quaternions to 72 axis-angle floats.
    const thetas: number[] = new Array(72).fill(0);
    for (let j = 0; j < 24; j++) {
      const q = localQuats[j];
      // Renormalize defensively.
      const norm = Math.sqrt(q.x * q.x + q.y * q.y + q.z * q.z + q.w * q.w);
      if (norm < 1e-8) continue;
      const w = Math.max(-1, Math.min(1, q.w / norm));
      const angle = 2 * Math.acos(w);
      const sinHalf = Math.sqrt(1 - w * w);
      if (sinHalf < 1e-6 || angle < 1e-6) continue;
      thetas[j * 3]     = (q.x / norm) / sinHalf * angle;
      thetas[j * 3 + 1] = (q.y / norm) / sinHalf * angle;
      thetas[j * 3 + 2] = (q.z / norm) / sinHalf * angle;
    }

    return {
      keyframe_id: `local-${tMs.toFixed(0)}`,
      betas: new Array(10).fill(0),
      thetas,
      mesh_url: null,
      processing_time_ms: 0,
      // bbox_norm + root_translation are filled in by the caller (the
      // pipeline already knows the rider's silhouette bbox and we keep this
      // module landmark-only).
      bbox_norm: null,
      root_translation: [midhip.x, midhip.y, midhip.z],
      bone_offsets: offsets,
      rider_height_m: this.dims?.height ?? null,
    };
  }
}
